import { identifyAnalyticsUser, trackActivity } from "../lib/analytics";
import { useLocation } from "react-router-dom";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  connectGoogleAccount,
  disconnectGoogleAccount,
  getCurrentUser,
  initialUser,
  loginUser,
  logoutUser,
  persistNormalizedUser,
  signUpUser,
} from "../api/auth";
import { clearFinanceAccess } from "../api/finance";
import { updateProfile as updateProfileRequest } from "../api/profile";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const { pathname } = useLocation();
  // The landing page and client galleries are anonymous; they never wait for a studio session.
  const isAnonymousPage = pathname === "/" || /^\/(gallery|select)\//.test(pathname);
  const bootstrapRequest = useRef(null);
  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    if (isAnonymousPage) return initialUser;
    const nextUser = await getCurrentUser();
    setUser(nextUser);
    return nextUser;
  }, [isAnonymousPage]);

  useEffect(() => {
    // Keep the next authenticated-route bootstrap pending until we leave it.
    if (isAnonymousPage) {
      setUser(initialUser);
      setLoading(true);
      return undefined;
    }
    let isMounted = true;
    setLoading(true);
    // Reuse an in-flight request during StrictMode's effect replay.
    if (!bootstrapRequest.current) {
      bootstrapRequest.current = getCurrentUser().finally(() => {
        bootstrapRequest.current = null;
      });
    }

    const bootstrapUser = async () => {
      try {
        const nextUser = await bootstrapRequest.current;
        if (isMounted) {
          setUser(nextUser);
        }
      } catch {
        if (isMounted) setUser(initialUser);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    bootstrapUser();

    return () => {
      isMounted = false;
    };
  }, [isAnonymousPage]);

  const signIn = useCallback(async (credentials) => {
    const nextUser = await loginUser(credentials);
    identifyAnalyticsUser(nextUser.id);
    trackActivity("login");
    setUser(nextUser);
    return nextUser;
  }, []);

  const signUp = useCallback(async (payload) => {
    const nextUser = await signUpUser(payload);
    identifyAnalyticsUser(nextUser.id);
    trackActivity("sign_up");
    setUser(nextUser);
    return nextUser;
  }, []);

  const connectGoogle = useCallback(async () => {
    const result = await connectGoogleAccount();

    if (result.mode === "redirect" && result.url) {
      window.location.assign(result.url);
      return result;
    }

    if (result.user) {
      setUser(result.user);
    }

    return result;
  }, []);

  const disconnectGoogle = useCallback(async () => {
    const nextUser = await disconnectGoogleAccount();
    setUser(nextUser);
    return nextUser;
  }, []);

  const updateProfile = useCallback(async (payload) => {
    const profile = await updateProfileRequest(payload);
    const nextUser = persistNormalizedUser(profile);
    setUser(nextUser);
    return nextUser;
  }, []);

  const signOut = useCallback(async () => {
    clearFinanceAccess();
    const nextUser = await logoutUser();
    trackActivity("logout");
    identifyAnalyticsUser(null);
    setUser(nextUser);
    return nextUser;
  }, []);

  const value = useMemo(
    () => ({
      user: isAnonymousPage ? initialUser : user,
      loading: isAnonymousPage ? false : loading,
      setUser,
      refreshUser,
      signIn,
      signUp,
      signOut,
      connectGoogle,
      disconnectGoogle,
      updateProfile,
    }),
    [
      isAnonymousPage,
      user,
      loading,
      refreshUser,
      signIn,
      signUp,
      signOut,
      connectGoogle,
      disconnectGoogle,
      updateProfile,
    ],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
